import _ from 'underscore';

import {assert} from 'Asserts';
import ExportAuditLogModal from 'audit/ExportAuditLogModal';
import AuditLogViewModal from 'audit/view-modal/AuditLogViewModal';
import {setLocationHref} from 'BenchlingUtil';
import notifications from 'notifications';
import runModal from 'ui-library/runModal';

import {
  EXPORT_LOCATION_HISTORY_MODAL_TITLE,
  EXPORT_PLATE_HISTORY_MODAL_TITLE,
  EXPORT_SAMPLE_CONTAINER_HISTORY_MODAL_TITLE,
} from './AuditConstants';
import * as AuditDAO from './AuditDAO';
import {
  AuditLogFormat,
  ExportAuditLogsResponse,
  ExportHistoryReportResponse,
  ExportAuditLogsDeferredResponse,
} from './AuditDAO';

export function exportAuditLogs(
  ids: Array<string>,
  additionalOptions: {resultsWarehouseIds?: Array<string>; relatedResultsModelId?: string} = {}
): void {
  runModal(AuditLogViewModal, {
    ids,
    additionalOptions,
  });
}

export function exportSampleContainerHistory(ids: Array<string>): void {
  runModal(ExportAuditLogModal, {
    onRequestSubmit: (params: {format: AuditLogFormat}) => {
      initiateSampleContainerHistoryExport(ids, params.format);
    },
    modalTitle: EXPORT_SAMPLE_CONTAINER_HISTORY_MODAL_TITLE,
    dropdownDisplayString: 'Select report format...',
  });
}

export function exportPlateHistoryReport(ids: Array<string>): void {
  runModal(ExportAuditLogModal, {
    onRequestSubmit: (params: {format: AuditLogFormat}) => {
      initiatePlateHistoryExport(ids, params.format);
    },
    modalTitle: EXPORT_PLATE_HISTORY_MODAL_TITLE,
    dropdownDisplayString: 'Select report format...',
  });
}

export function exportDataModsTACReport(): void {
  runModal(ExportAuditLogModal, {
    onRequestSubmit: (params: {format: AuditLogFormat}) => {
      initiateDataModsTACReport(params.format);
    },
  });
}

export function exportLocationHistoryReport(ids: Array<string>): void {
  runModal(ExportAuditLogModal, {
    onRequestSubmit: (params: {format: AuditLogFormat; startTime: string; endTime: string}) => {
      initiateLocationHistoryExport(ids, params.format, params.startTime, params.endTime);
    },
    modalTitle: EXPORT_LOCATION_HISTORY_MODAL_TITLE,
    dropdownDisplayString: 'Select report format...',
    showDateRangeSelect: true,
  });
}

export function exportIPAllowlistsTACReport(): void {
  runModal(ExportAuditLogModal, {
    onRequestSubmit: (params: {format: AuditLogFormat}) => {
      initiateIPAllowlistsTACReport(params.format);
    },
  });
}

export function exportAISettingsTACReport(): void {
  runModal(ExportAuditLogModal, {
    onRequestSubmit: (params: {format: AuditLogFormat}) => {
      initiateAISettingsTACReport(params.format);
    },
  });
}

export function exportTenantCapabilitiesReport(): void {
  runModal(ExportAuditLogModal, {
    onRequestSubmit: (params: {format: AuditLogFormat}) => {
      initiateTenantCapabilitiesReport(params.format);
    },
  });
}

export function exportAuthenticationOptionsReport(): void {
  runModal(ExportAuditLogModal, {
    onRequestSubmit: (params: {format: AuditLogFormat}) => {
      initiateAuthenticationOptionsReport(params.format);
    },
  });
}

export function initiateAuditLogExport(
  ids: Array<string>,
  format: AuditLogFormat,
  additionalOptions: {resultsWarehouseIds?: Array<string>; relatedResultsModelId?: string} = {}
): void {
  if (additionalOptions.relatedResultsModelId != null) {
    assert(ids.length === 0, 'No ids should be specified if relatedResultsModelId is defined');
    initiateRelatedResultsAuditLogExport(additionalOptions.relatedResultsModelId, format);
  } else {
    initiateBaseAuditLogExport(ids, format, additionalOptions);
  }
}

function initiateBaseAuditLogExport(
  ids: Array<string>,
  format: AuditLogFormat,
  additionalOptions: {resultsWarehouseIds?: Array<string>} = {}
): void {
  const notificationId = _.uniqueId('export-audit-logs-');
  notifications.info('Processing export. Audit log exports exceeding 5 minutes will be emailed.', {
    id: notificationId,
    duration: 0,
  });
  AuditDAO.exportAuditLogs(ids, format, additionalOptions)
    .done(({url, message}: ExportAuditLogsDeferredResponse) => {
      if (typeof url === 'string') {
        setLocationHref(url);
        notifications.success('Export complete. Downloading...', {id: notificationId});
      }
      if (typeof message === 'string' && message.length > 0) {
        notifications.warn(message, {id: notificationId});
      }
    })
    .fail((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

function initiateRelatedResultsAuditLogExport(id: string, format: AuditLogFormat): void {
  const notificationId = _.uniqueId('export-related-results-audit-logs-');
  notifications.info('Processing export. Audit log exports exceeding 5 minutes will be emailed.', {
    id: notificationId,
    duration: 0,
  });
  AuditDAO.exportRelatedResultsAuditLogs(id, format)
    .done(({url, message}: ExportAuditLogsDeferredResponse) => {
      if (typeof url === 'string') {
        setLocationHref(url);
        notifications.success('Export complete. Downloading...', {id: notificationId});
      }
      if (typeof message === 'string' && message.length > 0) {
        notifications.warn(message, {id: notificationId});
      }
    })
    .fail((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

export function initiateSampleContainerHistoryExport(ids: Array<string>, format: AuditLogFormat): void {
  const notificationId = _.uniqueId('export-sample-container-history-report-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  AuditDAO.exportSampleContainerHistory(ids, format)
    .done(({succeeded, url, message}: ExportHistoryReportResponse) => {
      if (succeeded) {
        setLocationHref(url);
        notifications.success('Export complete. Downloading...', {id: notificationId});
      } else {
        notifications.warn(message);
      }
    })
    .fail((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

export function initiatePlateHistoryExport(ids: Array<string>, format: AuditLogFormat): void {
  const notificationId = _.uniqueId('export-plate-history-report-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  AuditDAO.exportPlateHistory(ids, format)
    .done(({succeeded, url, message}: ExportHistoryReportResponse) => {
      if (succeeded) {
        setLocationHref(url);
        notifications.success('Export complete. Downloading...', {id: notificationId});
      } else {
        notifications.warn(message);
      }
    })
    .fail((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

export function initiateDataModsTACReport(format: AuditLogFormat): void {
  const notificationId = _.uniqueId('export-data-mods-settings-logs-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  AuditDAO.exportDataModsTACReport(format)
    .done(({url}: ExportAuditLogsResponse) => {
      setLocationHref(url);
      notifications.success('Export complete. Downloading...', {id: notificationId});
    })
    .fail((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

export function initiateLocationHistoryExport(
  ids: Array<string>,
  format: AuditLogFormat,
  startTime?: string,
  endTime?: string
): void {
  const notificationId = _.uniqueId('export-location-history-report-');
  notifications.info('Starting export...', {id: notificationId, duration: 0});

  AuditDAO.exportLocationHistoryReport(ids, format, startTime, endTime)
    .then(() => {
      notifications.success(
        'Your Location History Report export is running, and may take some time. You will receive an email when it is complete.',
        {id: notificationId}
      );
    })
    .catch((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

export function initiateIPAllowlistsTACReport(format: AuditLogFormat): void {
  const notificationId = _.uniqueId('export-ip-allowlists-settings-logs-');
  notifications.info('Processing export. Audit log exports exceeding 5 minutes will be emailed.', {
    id: notificationId,
    duration: 0,
  });
  AuditDAO.exportIPAllowlistsTACReport(format)
    .done(({url, message}: ExportAuditLogsDeferredResponse) => {
      if (typeof url === 'string') {
        setLocationHref(url);
        notifications.success('Export complete. Downloading...', {id: notificationId});
      }
      if (typeof message === 'string' && message.length > 0) {
        notifications.warn(message, {id: notificationId});
      }
    })
    .fail((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

export function initiateAISettingsTACReport(format: AuditLogFormat): void {
  const notificationId = _.uniqueId('export-ai-settings-logs-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  AuditDAO.exportAISettingsTACReport(format)
    .done(({url}: ExportAuditLogsResponse) => {
      setLocationHref(url);
      notifications.success('Export complete. Downloading...', {id: notificationId});
    })
    .fail((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

export function initiateTenantCapabilitiesReport(format: AuditLogFormat): void {
  const notificationId = _.uniqueId('export-tenant-capabilities-logs-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  AuditDAO.exportTenantCapabilitiesReport(format)
    .done(({url}: ExportAuditLogsResponse) => {
      setLocationHref(url);
      notifications.success('Export complete. Downloading...', {id: notificationId});
    })
    .fail((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}

export function initiateAuthenticationOptionsReport(format: AuditLogFormat): void {
  const notificationId = _.uniqueId('export-authentication-options-logs-');
  notifications.info('Exporting...', {id: notificationId, duration: 0});
  AuditDAO.exportAuthenticationOptionsReport(format)
    .done(({url}: ExportAuditLogsResponse) => {
      setLocationHref(url);
      notifications.success('Export complete. Downloading...', {id: notificationId});
    })
    .fail((response: Response) => {
      notifications.notifyError(response, {id: notificationId});
    });
}
